import { Modal } from "rsuite";
import { useState } from "react";
import { Button } from "reactstrap";

import QuickNoteHeader from "./QuickNoteHeader";
import QuickNotePreview from "./QuickNotePreview";
import QuickNoteRecording from "./QuickNoteRecording/QuickNoteRecording";
import TypeQuickNote from "./QuickNoteRecording/TypeQuickNote";
import QuickNoteSaving from "./QuickNoteSaving";
import useTranscript from "../../../api/api-hooks/tools/useTranscript";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";

const QuickNoteModal = ({
  open,
  setOpen,
  useBrieDication = false,
  header = "Quick Note",
  body = "Click to Start Recording",
  saveTranscription,
  initializeRecording = false,
  instantSave = false,
  showSuccess = false,
  record, // For automatically filling the related to field.
  onClose,
}) => {
  const isPublicForm = window.location.pathname.startsWith("/forms");
  const [page, setPage] = useState("recording"); // "recording", "text", "saving", "preview", "saving-quick-note", "saved"

  const { fetchTranscript, cancelTranscript, deleteTranscript } =
    useTranscript();
  const [recordingData, setRecordingData] = useState(null);
  const [recordingURL, setRecordingURL] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [text, setText] = useState("");
  const [recordID, setRecordID] = useState(null);

  // Disable brie related features when in a public form.
  if (isPublicForm) return null;

  return (
    <>
      <Modal open={open} size="xs">
        <QuickNoteHeader header={header} onClose={closeModal} />
        {page === "recording" && (
          <QuickNoteRecording
            setPage={setPage}
            recordingData={recordingData}
            setRecordingData={setRecordingData}
            text={text}
            setText={setText}
            fetchTranscript={onFetchTranscript}
            useBrieDictation={useBrieDication}
            body={body}
            initializeRecording={initializeRecording}
            instantSave={instantSave}
          />
        )}
        {page === "text" && (
          <TypeQuickNote
            text={text}
            setText={setText}
            goBack={() => {
              setPage("recording");
              setText("");
            }}
            goForward={() => setPage("preview")}
          />
        )}
        {page.startsWith("saving") && (
          <QuickNoteSaving
            page={page}
            recordID={recordID}
            closeModal={closeModal}
            action={"Audio"}
          />
        )}
        {page === "preview" && (
          <QuickNotePreview
            recordingURL={recordingURL}
            onCancelTranscript={onCancelTranscript}
            text={text}
            setText={setText}
            fileData={fileData}
            setPage={setPage}
            setRecordID={setRecordID}
            setFileData={setFileData}
            record={record}
          />
        )}
        {page === "saved" && (
          <div className="d-flex flex-column align-items-center p-4">
            <div className="text-center font-weight-500">
              Voice to text generation complete!
            </div>
            <div
              style={{
                border: "20px solid var(--primary)",
                borderRadius: "50%",
                width: 170,
                height: 170,
              }}
              className="mt-3 mb-4 d-flex justify-content-center align-items-center"
            >
              <i
                className="mdi mdi-check text-primary"
                style={{ fontSize: 100 }}
              />
            </div>
            <Button size="sm" color="primary" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        )}
      </Modal>
    </>
  );

  async function closeModal() {
    if (fileData?.recordID) {
      const cancelled = await onCancelTranscript();

      if (cancelled) return;
    }

    callFunctionIfExists(onClose);
    setOpen(false);
    setPage("recording");
    setRecordingData(null);
    setRecordingURL(null);
    setFileData(null);
    setText("");
    setRecordID(null);
  }

  function handleTranscriptSuccess(transcript) {
    setText(transcript);

    if (useBrieDication) {
      saveTranscription(transcript);

      if (showSuccess) {
        setPage("saved");
      } else {
        closeModal();
      }
    } else {
      setPage("preview");
    }
  }

  async function onFetchTranscript(audioBlob) {
    setPage("saving");

    const transcriptFileData = await fetchTranscript({
      audioBlob,
      onTranscriptSuccess: (transcript) => handleTranscriptSuccess(transcript),
      onError: () => {
        setPage("recording");
      },
      setFileData,
      setRecordingURL,
    });

    if (useBrieDication && transcriptFileData) {
      deleteTranscript({
        fileData: transcriptFileData,
        afterDelete: () => {
          setRecordingData(null);
        },
      });
    }
  }

  async function onCancelTranscript() {
    cancelTranscript({
      fileData,
      setFileData,
      afterDelete: () => {
        if (recordingData) {
          setPage("recording");
          setText("");
        } else {
          setPage("text");
        }

        setRecordingData(null);
        setRecordingURL(null);
      },
    });
  }
};

export default QuickNoteModal;
